<template>
    <LaWebsitePicker
        v-model="websitesModel"
        :websites="websites"
        :multiple="multiple"
        @onSelectDisabled="onSelectDisabled"
    />
    <portal to="dashboardTop" v-if="$mq.max.tablet">
        <div class="mb-4 mt-2">
            <LaBaseDatePicker v-model="periodModel" :tz="tz" class="ml-4" range />
        </div>
    </portal>
    <LaBaseDatePicker v-model="periodModel" class="ml-4" range />
</template>

<script>
import LaWebsitePicker from "@/components/filters/WebsitePicker";
import LaBaseDatePicker from "@/components/datepicker/BaseDatePicker";
import { mutationTypes as dialogMutationTypes } from "@/store/Dialog/types";
import { getterTypes as publisherGetterTypes } from "@/store/Publisher/types";
import store from "@/store/store";
import { usePropertyObjectModelProxy } from "@/helpers/data";
import { useRoute, useRouter } from "vue-router";
import { watch, computed } from "vue";
export default {
    name: "LaWebsiteDatePickers",
    components: {
        LaWebsitePicker,
        LaBaseDatePicker,
    },
    props: {
        modelValue: {
            type: Object,
            default: () => ({}),
        },
        websites: {
            type: Array,
            required: true,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        selectAllDefaults: {
            type: Boolean,
            default: false,
        },
        tz: {
            type: String,
        },
    },
    setup(props, { emit }) {
        let initialized = false;
        const periodModel = usePropertyObjectModelProxy("period", props, emit);
        const websitesModel = usePropertyObjectModelProxy("websites", props, emit);

        const onSelectDisabled = (website) => {
            store.commit(dialogMutationTypes.openDialog, {
                dialogName: "StartMonetizeDialog",
                dialogHeading: `Request monetization of ${website.name}`,
                item: website,
            });
        };
        const router = useRouter();
        const route = useRoute();
        const enabledWebs = computed(() => {
            return props.websites.filter((i) => !i.disabled);
        });

        watch(
            websitesModel,
            () => {
                const selectedIds = (websitesModel.value || []).map((i) => i.aab_site_id);
                const paramIds = route.params.webs ? route.params.webs.split(",").map((id) => Number(id.trim())) : null;

                if (!initialized) {
                    initialized = true;
                    // First mount: restrict the model to URL :webs only when the param
                    // is present. When the URL has no :webs, leave the model intact —
                    // it carries the LS-restored subset from the parent, and overwriting
                    // it with all available websites would wipe the user's selection on
                    // every remount (e.g. /dashboard → /fees → /dashboard).
                    if (paramIds) {
                        const restricted = props.websites.filter((i) => paramIds.includes(i.aab_site_id));
                        const restrictedKey = restricted
                            .map((i) => i.aab_site_id)
                            .sort()
                            .join(",");
                        const selectedKey = selectedIds.slice().sort().join(",");
                        if (restricted.length > 0 && selectedKey !== restrictedKey) {
                            websitesModel._setter(restricted);
                        }
                    }
                    return;
                }

                // After init: user changed selection — keep URL in sync.
                const selectedIdsString = selectedIds.join(",");
                if (selectedIdsString === (route.params.webs || "")) return;
                const newParams = { ...route.params };
                // After a remount (e.g. /fees → /dashboard) route.params.publisher
                // may be missing because the picker that owns the publisher hasn't
                // run router.replace yet. Without it, router.push assembles a URL
                // with :webs at the :publisher position (e.g. /dashboard/yield/661
                // instead of /dashboard/yield/685/661), and the dashboard then
                // parses 661 as the publisher id.
                if (!newParams.publisher) {
                    const mode = store.getters[publisherGetterTypes.getSelectedMode];
                    if (mode === "ltba") {
                        const p = store.getters[publisherGetterTypes.getSelectedPublisherLtba];
                        if (p) newParams.publisher = String(p.id);
                    } else if (mode === "yield") {
                        const yp = store.getters[publisherGetterTypes.getSelectedPublishersYield];
                        if (yp && yp.length > 0) {
                            newParams.publisher = yp.map((p) => p.id).join(",");
                        }
                    }
                }
                if (selectedIds.length === enabledWebs.value.length) {
                    delete newParams.webs;
                } else {
                    newParams.webs = selectedIdsString;
                }
                router.push({ name: route.name, params: newParams });
            },
            { immediate: true }
        );
        return { periodModel, websitesModel, onSelectDisabled };
    },
};
</script>

<style lang="scss" scoped>
::v-deep .select-trigger {
    min-width: 225px;
}
</style>
